import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import type {
  WorkshopQuote,
  WorkshopQuoteCreate,
  WorkshopQuoteDecisione,
  WorkshopQuoteBatchGroup,
  WorkshopQuoteContesto,
  WorkshopQuoteRispondi,
  RichiesteResponse,
} from '@/types/workshopQuotes'

const QK = (woId: number) => ['workshop-quotes', woId] as const

const formatApiError = (e: any, fallback = 'Errore'): string => {
  const detail = e?.response?.data?.detail
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d.msg ?? String(d)).join('; ') || fallback
  }
  return String(detail ?? fallback)
}

export const useWorkshopQuotes = (workOrderId: number, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: QK(workOrderId),
    queryFn: async () => {
      const res = await axiosInstance.get<WorkshopQuote[]>(`/work-orders/${workOrderId}/workshop-quotes`)
      return res.data
    },
    enabled: options?.enabled ?? true,
    staleTime: 10_000,
  })

export const useCreateWorkshopQuotes = (workOrderId: number) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: WorkshopQuoteCreate) => {
      const res = await axiosInstance.post<WorkshopQuote[]>(
        `/work-orders/${workOrderId}/workshop-quotes`,
        data,
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(workOrderId) })
      message.success('Richiesta di preventivo inviata')
    },
    onError: (e: any) => {
      message.error(formatApiError(e, 'Errore invio richiesta'))
    },
  })
}

export const useApprovaQuote = (workOrderId: number) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ quoteId, body }: { quoteId: number; body: WorkshopQuoteDecisione }) => {
      const res = await axiosInstance.post<WorkshopQuote>(`/workshop-quotes/${quoteId}/approva`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(workOrderId) })
      qc.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      message.success('Preventivo approvato')
    },
    onError: (e: any) => message.error(formatApiError(e)),
  })
}

export const useRespingiQuote = (workOrderId: number) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ quoteId, body }: { quoteId: number; body: WorkshopQuoteDecisione }) => {
      const res = await axiosInstance.post<WorkshopQuote>(`/workshop-quotes/${quoteId}/respingi`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(workOrderId) })
      qc.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      message.success('Preventivo respinto')
    },
    onError: (e: any) => message.error(formatApiError(e)),
  })
}

export const useRinegoziaQuote = (workOrderId: number) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ quoteId, body }: { quoteId: number; body: WorkshopQuoteDecisione }) => {
      const res = await axiosInstance.post<WorkshopQuote>(`/workshop-quotes/${quoteId}/rinegozia`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(workOrderId) })
      qc.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      message.success('Rinegoziazione inviata')
    },
    onError: (e: any) => message.error(formatApiError(e)),
  })
}

// ── Hook lato officina esterna ────────────────────────────────────────────────

export const useMieRichieste = () =>
  useQuery({
    queryKey: ['workshop-quotes-mie'],
    queryFn: async () => {
      const res = await axiosInstance.get<WorkshopQuoteBatchGroup[]>('/workshop-quotes/mie')
      return res.data
    },
    staleTime: 15_000,
  })

export const useContestoQuote = (quoteId: number, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: ['workshop-quote-contesto', quoteId],
    queryFn: async () => {
      const res = await axiosInstance.get<WorkshopQuoteContesto>(`/workshop-quotes/${quoteId}/contesto`)
      return res.data
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  })

export const useRispondiQuote = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ quoteId, body }: { quoteId: number; body: WorkshopQuoteRispondi }) => {
      const res = await axiosInstance.post<WorkshopQuote>(`/workshop-quotes/${quoteId}/rispondi`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshop-quotes-mie'] })
      message.success('Risposta inviata')
    },
    onError: (e: any) => {
      message.error(formatApiError(e, 'Errore invio risposta'))
    },
  })
}

// ── Hook lato GM (vista richieste aggregate) ──────────────────────────────────

export const useRichiesteOfficine = (soloAperte = true) =>
  useQuery({
    queryKey: ['workshop-quotes-richieste', soloAperte],
    queryFn: async () => {
      const res = await axiosInstance.get<RichiesteResponse>('/workshop-quotes/richieste', {
        params: { solo_aperte: soloAperte },
      })
      return res.data
    },
    staleTime: 15_000,
  })

export const useApprovaQuoteRichieste = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ quoteId, body }: { quoteId: number; body: WorkshopQuoteDecisione }) => {
      const res = await axiosInstance.post<WorkshopQuote>(`/workshop-quotes/${quoteId}/approva`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshop-quotes-richieste'] })
      qc.invalidateQueries({ queryKey: ['work-order'] })
      message.success('Preventivo approvato')
    },
    onError: (e: any) => message.error(formatApiError(e)),
  })
}

export const useRespingiQuoteRichieste = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ quoteId, body }: { quoteId: number; body: WorkshopQuoteDecisione }) => {
      const res = await axiosInstance.post<WorkshopQuote>(`/workshop-quotes/${quoteId}/respingi`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshop-quotes-richieste'] })
      qc.invalidateQueries({ queryKey: ['work-order'] })
      message.success('Preventivo respinto')
    },
    onError: (e: any) => message.error(formatApiError(e)),
  })
}

export const useRinegoziaQuoteRichieste = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ quoteId, body }: { quoteId: number; body: WorkshopQuoteDecisione }) => {
      const res = await axiosInstance.post<WorkshopQuote>(`/workshop-quotes/${quoteId}/rinegozia`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshop-quotes-richieste'] })
      qc.invalidateQueries({ queryKey: ['work-order'] })
      message.success('Rinegoziazione inviata')
    },
    onError: (e: any) => message.error(formatApiError(e)),
  })
}

// ── Hook lato garage (batch) ──────────────────────────────────────────────────

export const useApprovaBatch = (workOrderId: number) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ batchId, body }: { batchId: string; body: WorkshopQuoteDecisione }) => {
      const res = await axiosInstance.post(`/workshop-quotes/batch/${batchId}/approva`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK(workOrderId) })
      qc.invalidateQueries({ queryKey: ['work-order', workOrderId] })
      message.success('Batch approvato')
    },
    onError: (e: any) => message.error(formatApiError(e)),
  })
}
